import { useEffect, useState } from 'react';
import { BetaPuls } from '@/components/BetaPuls';
import axios from 'axios';
import { Users, MessageCircle, Mail, MessageSquare, TrendingUp, LogOut, Database, Shield, Share2, Heart, Send, Sparkles } from 'lucide-react';
import { AdminOverview } from '@/pages/admin/AdminOverview';
import { AdminUsers } from '@/pages/admin/AdminUsers';
import { AdminCarma } from '@/pages/admin/AdminCarma';
import { AdminFeedback } from '@/pages/admin/AdminFeedback';
import { AdminInvites } from '@/pages/admin/AdminInvites';
import { AdminDiagnose } from '@/pages/admin/AdminDiagnose';
import { AdminModeration } from '@/pages/admin/AdminModeration';
import { AdminSharedLinks } from '@/pages/admin/AdminSharedLinks';
import { AdminMoments } from '@/pages/admin/AdminMoments';
import { AdminGratitude } from '@/pages/admin/AdminGratitude';
import { AdminBroadcast } from '@/pages/admin/AdminBroadcast';
import '@/pages/admin/admin.css';
import '@/components/mask/mask.css';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

export default function AdminV2() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [carma, setCarma] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [invites, setInvites] = useState([]);
  const [diagnose, setDiagnose] = useState(null);
  const [moderation, setModeration] = useState([]);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [momentsData, setMomentsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit User state
  const [editUser, setEditUser] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);

  // Admin Report state
  const [reportEmail, setReportEmail] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportResult, setReportResult] = useState(null);

  // Carma des Tages state
  const [cdtSending, setCdtSending] = useState(false);
  const [cdtResult, setCdtResult] = useState(null);

  useEffect(() => {
    async function checkAuth() {
      try {
        const res = await axios.get(`${API_V2}/admin/check`, { withCredentials: true });
        setAuthenticated(res.data.authenticated);
      } catch (_err) {
        setAuthenticated(false);
      } finally {
        setChecking(false);
      }
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    loadData();
  }, [authenticated, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'overview' || !stats) {
        const statsRes = await axios.get(`${API_V2}/admin/stats`, { withCredentials: true });
        setStats(statsRes.data);
      }
      if (activeTab === 'users') {
        const usersRes = await axios.get(`${API_V2}/admin/users`, { withCredentials: true });
        setUsers(usersRes.data);
      }
      if (activeTab === 'carma') {
        const carmaRes = await axios.get(`${API_V2}/admin/carma`, { withCredentials: true });
        setCarma(carmaRes.data);
      }
      if (activeTab === 'feedback') {
        const feedbackRes = await axios.get(`${API_V2}/admin/feedback`, { withCredentials: true });
        setFeedback(feedbackRes.data);
      }
      if (activeTab === 'invites') {
        const invitesRes = await axios.get(`${API_V2}/admin/invites`, { withCredentials: true });
        setInvites(invitesRes.data);
      }
      if (activeTab === 'diagnose') {
        const diagRes = await axios.get(`${API_V2}/admin/diagnose`, { withCredentials: true });
        setDiagnose(diagRes.data);
      }
      if (activeTab === 'moderation') {
        const modRes = await axios.get(`${API_V2}/admin/moderation`, { withCredentials: true });
        setModeration(modRes.data);
      }
      if (activeTab === 'shared') {
        const sharedRes = await axios.get(`${API_V2}/admin/shared-links`, { withCredentials: true });
        setSharedLinks(sharedRes.data);
      }
      if (activeTab === 'moments') {
        const momentsRes = await axios.get(`${API_V2}/admin/moments`, { withCredentials: true });
        setMomentsData(momentsRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden');
      if (err.response?.status === 401) {
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoggingIn(true);
    setLoginError(null);
    try {
      await axios.post(`${API_V2}/admin/login`, { password }, { withCredentials: true });
      setAuthenticated(true);
      setPassword('');
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Login fehlgeschlagen');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_V2}/admin/logout`, {}, { withCredentials: true });
    } catch (err) { console.error('Logout failed:', err); /* logout always proceeds */ }
    setAuthenticated(false);
    setStats(null);
    setUsers([]);
    setCarma([]);
    setFeedback([]);
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditEmail(u.email || '');
    setEditName(u.name || '');
    setEditError(null);
    setEditSuccess(null);
  };

  const saveEditUser = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setEditError(null);
    setEditSuccess(null);
    try {
      const payload = {};
      if (editEmail.trim() !== editUser.email) payload.email = editEmail.trim();
      if (editName.trim() !== (editUser.name || '')) payload.name = editName.trim();
      if (Object.keys(payload).length === 0) {
        setEditError('Keine Änderungen.');
        setEditSaving(false);
        return;
      }
      await axios.put(`${API_V2}/admin/users/${editUser.id}`, payload, { withCredentials: true });
      setEditSuccess('Gespeichert!');
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...payload } : u));
      setTimeout(() => setEditUser(null), 1200);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setEditSaving(false);
    }
  };

  const sendAdminReport = async () => {
    if (!reportEmail.trim() || !reportEmail.includes('@')) return;
    setReportSending(true);
    setReportResult(null);
    try {
      const res = await axios.post(`${API_V2}/admin/send-report`, { email: reportEmail.trim() }, { withCredentials: true });
      if (res.data.success) {
        setReportResult({ type: 'success', message: `Report gesendet an ${reportEmail.trim()}` });
      } else {
        setReportResult({ type: 'error', message: res.data.error || 'Fehler beim Senden' });
      }
    } catch (err) {
      setReportResult({ type: 'error', message: err.response?.data?.detail || 'Fehler beim Senden' });
    } finally {
      setReportSending(false);
    }
  };

  const triggerCarmaDesTages = async () => {
    setCdtSending(true);
    setCdtResult(null);
    try {
      const res = await axios.post(`${API_V2}/admin/trigger-carma-des-tages`, {}, { withCredentials: true });
      if (res.data.success) {
        setCdtResult({ type: 'success', message: `${res.data.users_notified} User benachrichtigt, ${res.data.users_skipped} übersprungen` });
      } else {
        setCdtResult({ type: 'error', message: 'Fehler beim Auslösen' });
      }
    } catch (err) {
      setCdtResult({ type: 'error', message: err.response?.data?.detail || 'Fehler beim Auslösen' });
    } finally {
      setCdtSending(false);
    }
  };

  if (checking) {
    return (
      <div className="cc-admin min-h-screen flex items-center justify-center">
        <div className="text-[var(--muted)] text-sm">Einen Moment …</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="cc-admin relative min-h-screen flex items-center justify-center overflow-x-hidden">
        <div className="mask-bg-word" aria-hidden="true"><span style={{ transform: 'translateX(-6%)' }}>ADMIN</span></div>
        <div className="relative z-10 w-full max-w-sm px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">CarmaCircle</p>
          <h1 className="mask-headline text-3xl mb-8">Admin</h1>
          <form onSubmit={handleLogin} className="space-y-5">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin-Passwort"
              className="w-full rounded-lg border border-gray-600 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            {loginError && <div className="text-red-400 text-sm">{loginError}</div>}
            <button
              type="submit"
              disabled={loggingIn || !password}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loggingIn ? 'Anmelden …' : 'Anmelden'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Übersicht', icon: TrendingUp },
    { id: 'broadcast', label: 'Rundmail', icon: Send },
    { id: 'users', label: 'User', icon: Users },
    { id: 'carma', label: 'Carma', icon: MessageCircle },
    { id: 'moments', label: 'Momente', icon: Sparkles },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    { id: 'invites', label: 'Einladungen', icon: Mail },
    { id: 'shared', label: 'Geteilte Links', icon: Share2 },
    { id: 'diagnose', label: 'Diagnose', icon: Database },
    { id: 'moderation', label: 'Moderation', icon: Shield },
    { id: 'gratitude', label: 'Dankbarkeit', icon: Heart },
  ];

  return (
    <div className="cc-admin min-h-screen">
      <header className="px-6 pt-6 pb-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-baseline gap-3">
          <h1 className="mask-headline text-2xl">Admin</h1>
          <span className="text-[10px] tracking-[0.14em] uppercase text-[var(--muted)]">CarmaCircle</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-[var(--muted)] hover:text-[var(--text)] text-sm transition-colors"
        >
          <LogOut size={15} strokeWidth={1.5} />
          Abmelden
        </button>
      </header>

      <div className="flex max-w-6xl mx-auto">
        <nav className="w-52 shrink-0 p-4 space-y-0.5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'text-[var(--text)] bg-black/5 font-medium'
                  : 'text-[var(--muted)] hover:text-[var(--text)]'
              }`}
            >
              <tab.icon size={16} strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </nav>

        <main className="flex-1 p-6 min-w-0">
          {loading && <div className="text-[var(--muted)] text-sm">Einen Moment …</div>}
          {error && <div className="text-red-400 mb-4">{error}</div>}
          {activeTab === 'broadcast' && <AdminBroadcast userCount={stats?.totalUsers || stats?.users || null} />}
          {activeTab === 'overview' && (
            <>
            <AdminOverview
              stats={stats}
              reportEmail={reportEmail}
              setReportEmail={setReportEmail}
              reportSending={reportSending}
              reportResult={reportResult}
              sendAdminReport={sendAdminReport}
              cdtSending={cdtSending}
              cdtResult={cdtResult}
              triggerCarmaDesTages={triggerCarmaDesTages}
            />
            <BetaPuls />
            </>
          )}
          {activeTab === 'users' && (
            <AdminUsers
              users={users}
              openEditUser={openEditUser}
              editUser={editUser}
              setEditUser={setEditUser}
              editName={editName}
              setEditName={setEditName}
              editEmail={editEmail}
              setEditEmail={setEditEmail}
              editError={editError}
              editSuccess={editSuccess}
              editSaving={editSaving}
              saveEditUser={saveEditUser}
            />
          )}
          {activeTab === 'carma' && <AdminCarma carma={carma} />}
          {activeTab === 'moments' && <AdminMoments data={momentsData} />}
          {activeTab === 'feedback' && <AdminFeedback feedback={feedback} />}
          {activeTab === 'invites' && <AdminInvites invites={invites} />}
          {activeTab === 'shared' && <AdminSharedLinks sharedLinks={sharedLinks} />}
          {activeTab === 'diagnose' && <AdminDiagnose diagnose={diagnose} loading={loading} />}
          {activeTab === 'moderation' && <AdminModeration moderation={moderation} setModeration={setModeration} />}
          {activeTab === 'gratitude' && <AdminGratitude />}
        </main>
      </div>
    </div>
  );
}
